import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Wrench, 
  Calendar, 
  FileText, 
  LogOut, 
  User,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Settings,
  Save,
  MapPin,
  Phone,
  Globe,
  Camera,
  Image,
  Upload,
  Trash2,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

const MechanicDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingPhotos, setBookingPhotos] = useState({ before_photos: [], after_photos: [] });
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  
  const [invoiceForm, setInvoiceForm] = useState({
    labor_hours: '',
    labor_rate: '',
    parts: [],
    notes: ''
  });
  const [newPart, setNewPart] = useState({ name: '', quantity: 1, unit_price: '' });
  
  // Profile editing form
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    address: '',
    phone: '',
    business_website: '',
    services: '',
    is_bilingual: false,
    languages: '',
    service_areas: ''
  });
  
  // Availability form
  const [availability, setAvailability] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [profileRes, bookingsRes, invoicesRes, availRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanic/profile`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/mechanic/bookings`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/mechanic/invoices`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/api/mechanic/availability`, { headers, withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
      setAvailability(availRes.data.availability || []);
      
      // Initialize profile form
      setProfileForm({
        business_name: profileRes.data.business_name || '',
        address: profileRes.data.address || '',
        phone: profileRes.data.phone || '',
        business_website: profileRes.data.business_website || '',
        services: profileRes.data.services?.join(', ') || '',
        is_bilingual: profileRes.data.is_bilingual || false,
        languages: profileRes.data.languages?.join(', ') || '',
        service_areas: profileRes.data.service_areas?.join(', ') || ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        toast.error('Mechanic profile not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/mechanic/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Booking ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAddPart = () => {
    if (!newPart.name || !newPart.unit_price) return;
    setInvoiceForm({
      ...invoiceForm,
      parts: [...invoiceForm.parts, { ...newPart, unit_price: parseFloat(newPart.unit_price) }]
    });
    setNewPart({ name: '', quantity: 1, unit_price: '' });
  };

  const handleRemovePart = (idx) => {
    setInvoiceForm({
      ...invoiceForm,
      parts: invoiceForm.parts.filter((_, i) => i !== idx)
    });
  };

  const handleCreateInvoice = async () => {
    if (!selectedBooking) return;

    try {
      await axios.post(
        `${API_URL}/api/mechanic/invoices`,
        {
          booking_id: selectedBooking.booking_id,
          labor_hours: parseFloat(invoiceForm.labor_hours),
          labor_rate: parseFloat(invoiceForm.labor_rate),
          parts: invoiceForm.parts,
          notes: invoiceForm.notes
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Invoice created successfully');
      setShowInvoiceDialog(false);
      setInvoiceForm({ labor_hours: '', labor_rate: '', parts: [], notes: '' });
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updates = {
        business_name: profileForm.business_name,
        address: profileForm.address,
        phone: profileForm.phone,
        business_website: profileForm.business_website || null,
        services: profileForm.services.split(',').map(s => s.trim()).filter(Boolean),
        is_bilingual: profileForm.is_bilingual,
        languages: profileForm.languages ? profileForm.languages.split(',').map(s => s.trim()).filter(Boolean) : [],
        service_areas: profileForm.service_areas ? profileForm.service_areas.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : []
      };
      
      await axios.put(`${API_URL}/api/mechanic/profile`, updates, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success('Profile updated successfully');
      setShowProfileDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveAvailability = async () => {
    setSavingAvailability(true);
    try {
      await axios.put(`${API_URL}/api/mechanic/availability`, availability, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Availability updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  const toggleDayAvailability = (dayIndex) => {
    const existingSlot = availability.find(s => s.day_of_week === dayIndex);
    if (existingSlot) {
      setAvailability(availability.filter(s => s.day_of_week !== dayIndex));
    } else {
      setAvailability([...availability, { day_of_week: dayIndex, start_time: '09:00', end_time: '17:00' }]);
    }
  };

  const updateSlotTime = (dayIndex, field, value) => {
    setAvailability(availability.map(s => 
      s.day_of_week === dayIndex ? { ...s, [field]: value } : s
    ));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Photo upload functions
  const openPhotoDialog = async (booking) => {
    setSelectedBooking(booking);
    setShowPhotoDialog(true);
    await fetchBookingPhotos(booking.booking_id);
  };

  const fetchBookingPhotos = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bookings/${bookingId}/photos`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setBookingPhotos(response.data);
    } catch (error) {
      setBookingPhotos({ before_photos: [], after_photos: [] });
    }
  };

  const handlePhotoUpload = async (photoType, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    try {
      await axios.post(
        `${API_URL}/api/mechanic/bookings/${selectedBooking.booking_id}/photos?photo_type=${photoType}`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );
      toast.success(`${photoType === 'before' ? 'Before' : 'After'} photos uploaded`);
      await fetchBookingPhotos(selectedBooking.booking_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/mechanic/photos/${photoId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Photo deleted');
      await fetchBookingPhotos(selectedBooking.booking_id);
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      paid: 'bg-green-100 text-green-800 border-green-200'
    };
    return (
      <Badge className={`${statusClasses[status] || 'bg-gray-100'} border font-medium`}>
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowProfileDialog(true)} 
                className="text-white hover:bg-slate-800"
                data-testid="profile-settings-btn"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2 text-white">
                <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <span className="hidden sm:inline text-sm">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-slate-800" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-['Barlow'] text-3xl font-bold text-slate-900" data-testid="dashboard-title">
            {profile?.business_name || 'Mechanic Dashboard'}
          </h1>
          <p className="text-slate-600 mt-1">Manage your bookings, invoices, and availability</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{bookings.length}</p>
                  <p className="text-sm text-slate-600">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-sm flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {bookings.filter(b => b.status === 'pending').length}
                  </p>
                  <p className="text-sm text-slate-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {bookings.filter(b => b.status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    ${invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0).toFixed(0)}
                  </p>
                  <p className="text-sm text-slate-600">Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              <FileText className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">
              <Clock className="w-4 h-4 mr-2" />
              Availability
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No bookings yet</h3>
                  <p className="text-slate-600">New bookings will appear here</p>
                </CardContent>
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.booking_id} className="bg-white border border-slate-200" data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-['Barlow'] text-lg font-bold text-slate-900">
                            {booking.service_type}
                          </h3>
                          {getStatusBadge(booking.status)}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                          <p><span className="font-medium">Customer:</span> {booking.customer_name}</p>
                          <p><span className="font-medium">Vehicle:</span> {booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}</p>
                          <p><span className="font-medium">Date:</span> {booking.preferred_date}</p>
                          <p><span className="font-medium">Time:</span> {booking.preferred_time}</p>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">
                          <span className="font-medium">Issue:</span> {booking.description}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {booking.status === 'pending' && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleStatusUpdate(booking.booking_id, 'confirmed')}
                              data-testid={`confirm-${booking.booking_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Confirm
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 border-red-200"
                              onClick={() => handleStatusUpdate(booking.booking_id, 'cancelled')}
                              data-testid={`cancel-${booking.booking_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => handleStatusUpdate(booking.booking_id, 'in_progress')}
                          >
                            Start Work
                          </Button>
                        )}
                        {booking.status === 'in_progress' && (
                          <Button 
                            size="sm" 
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setInvoiceForm({ ...invoiceForm, labor_rate: 75 });
                              setShowInvoiceDialog(true);
                            }}
                            data-testid={`create-invoice-${booking.booking_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Create Invoice
                          </Button>
                        )}
                        {/* Photo Upload Button - available for confirmed, in_progress, completed */}
                        {['confirmed', 'in_progress', 'completed'].includes(booking.status) && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-slate-300"
                            onClick={() => openPhotoDialog(booking)}
                            data-testid={`photos-${booking.booking_id}`}
                          >
                            <Camera className="w-4 h-4 mr-1" />
                            Photos
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 ? (
              <Card className="bg-white border border-slate-200">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No invoices yet</h3>
                  <p className="text-slate-600">Create invoices for completed work</p>
                </CardContent>
              </Card>
            ) : (
              invoices.map((invoice) => (
                <Card key={invoice.invoice_id} className="bg-white border border-slate-200" data-testid={`invoice-${invoice.invoice_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-['JetBrains_Mono'] text-sm text-slate-900">
                            #{invoice.invoice_id}
                          </h3>
                          {getStatusBadge(invoice.status)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Labor</p>
                            <p className="font-medium">{invoice.labor_hours}hrs × ${invoice.labor_rate}</p>
                            <p className="font-bold text-slate-900">${invoice.labor_total?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Parts</p>
                            <p className="font-bold text-slate-900">${invoice.parts_total?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Tax (8%)</p>
                            <p className="font-bold text-slate-900">${invoice.tax?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Total</p>
                            <p className="font-bold text-orange-600 text-lg">${invoice.total?.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      {invoice.status === 'draft' && (
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API_URL}/api/mechanic/invoices/${invoice.invoice_id}/send`,
                                {},
                                { headers: getAuthHeaders(), withCredentials: true }
                              );
                              toast.success('Invoice sent to customer');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to send invoice');
                            }
                          }}
                        >
                          Send to Customer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" className="space-y-4">
            <Card className="bg-white border border-slate-200" data-testid="availability-card">
              <CardHeader>
                <CardTitle className="font-['Barlow'] text-xl flex items-center justify-between">
                  <span>Weekly Availability</span>
                  <Button 
                    onClick={handleSaveAvailability}
                    disabled={savingAvailability}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="save-availability-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingAvailability ? 'Saving...' : 'Save'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {DAYS_OF_WEEK.map((day, idx) => {
                  const slot = availability.find(s => s.day_of_week === idx);
                  const isAvailable = !!slot;
                  
                  return (
                    <div 
                      key={day} 
                      className={`flex items-center justify-between p-4 rounded-md border ${
                        isAvailable ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`day-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <Switch
                          checked={isAvailable}
                          onCheckedChange={() => toggleDayAvailability(idx)}
                          data-testid={`toggle-${idx}`}
                        />
                        <span className={`font-medium ${isAvailable ? 'text-green-800' : 'text-slate-600'}`}>
                          {day}
                        </span>
                      </div>
                      
                      {isAvailable && (
                        <div className="flex items-center gap-2">
                          <select
                            value={slot.start_time}
                            onChange={(e) => updateSlotTime(idx, 'start_time', e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                          >
                            {TIME_SLOTS.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="text-slate-500">to</span>
                          <select
                            value={slot.end_time}
                            onChange={(e) => updateSlotTime(idx, 'end_time', e.target.value)}
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                          >
                            {TIME_SLOTS.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Invoice Creation Dialog */}
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="max-w-lg bg-white" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Barlow'] text-xl">Create Invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Labor Hours</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={invoiceForm.labor_hours}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, labor_hours: e.target.value })}
                    data-testid="labor-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    value={invoiceForm.labor_rate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, labor_rate: e.target.value })}
                    data-testid="labor-rate-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Parts</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Part name"
                    value={newPart.name}
                    onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={newPart.quantity}
                    onChange={(e) => setNewPart({ ...newPart, quantity: parseInt(e.target.value) || 1 })}
                    className="w-16"
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    value={newPart.unit_price}
                    onChange={(e) => setNewPart({ ...newPart, unit_price: e.target.value })}
                    className="w-24"
                  />
                  <Button type="button" onClick={handleAddPart} size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {invoiceForm.parts.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {invoiceForm.parts.map((part, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                        <span>{part.name} x{part.quantity}</span>
                        <div className="flex items-center gap-2">
                          <span>${(part.quantity * part.unit_price).toFixed(2)}</span>
                          <button onClick={() => handleRemovePart(idx)} className="text-red-500 hover:text-red-700">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  placeholder="Additional notes..."
                />
              </div>

              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleCreateInvoice}
                data-testid="create-invoice-submit"
              >
                Create Invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile Editing Dialog */}
        <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
          <DialogContent className="max-w-lg bg-white" data-testid="profile-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Barlow'] text-xl">Edit Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Business Name
                </Label>
                <Input
                  value={profileForm.business_name}
                  onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                  data-testid="profile-business-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </Label>
                <Input
                  value={profileForm.address}
                  onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                  data-testid="profile-address"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone
                </Label>
                <Input
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  data-testid="profile-phone"
                />
              </div>

              <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="profile-bilingual"
                    checked={profileForm.is_bilingual}
                    onCheckedChange={(checked) => setProfileForm({ ...profileForm, is_bilingual: checked })}
                    data-testid="profile-bilingual"
                  />
                  <Label htmlFor="profile-bilingual" className="cursor-pointer">I am bilingual / multilingual</Label>
                </div>
                {profileForm.is_bilingual && (
                  <div className="ml-6">
                    <Label className="text-sm text-slate-600">Languages (comma-separated)</Label>
                    <Input
                      value={profileForm.languages}
                      onChange={(e) => setProfileForm({ ...profileForm, languages: e.target.value })}
                      placeholder="English, Spanish, French..."
                      className="mt-1"
                      data-testid="profile-languages"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Website (Optional)
                </Label>
                <Input
                  value={profileForm.business_website}
                  onChange={(e) => setProfileForm({ ...profileForm, business_website: e.target.value })}
                  placeholder="https://..."
                  data-testid="profile-website"
                />
              </div>

              <div className="space-y-2">
                <Label>Services (comma-separated)</Label>
                <Textarea
                  value={profileForm.services}
                  onChange={(e) => setProfileForm({ ...profileForm, services: e.target.value })}
                  placeholder="Oil Change, Brake Service, Engine Repair..."
                  rows={3}
                  data-testid="profile-services"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Service Areas (Postal Codes)
                </Label>
                <Textarea
                  value={profileForm.service_areas}
                  onChange={(e) => setProfileForm({ ...profileForm, service_areas: e.target.value })}
                  placeholder="M5V, M4Y, L5B, L5N..."
                  rows={2}
                  data-testid="profile-service-areas"
                />
                <p className="text-xs text-slate-500">
                  Enter postal code prefixes you serve, separated by commas. Customers will find you based on these areas.
                </p>
              </div>

              <Button 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Upload Dialog */}
        <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="photo-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
                <Camera className="w-5 h-5 text-orange-500" />
                Repair Photos
              </DialogTitle>
              {selectedBooking && (
                <p className="text-sm text-slate-500">
                  {selectedBooking.service_type} - {selectedBooking.vehicle_make} {selectedBooking.vehicle_model}
                </p>
              )}
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Before Photos */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    Before Repair
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload('before', e.target.files)}
                      disabled={uploadingPhotos}
                    />
                    <Button size="sm" variant="outline" asChild disabled={uploadingPhotos}>
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingPhotos ? 'Uploading...' : 'Upload'}
                      </span>
                    </Button>
                  </label>
                </div>
                
                {bookingPhotos.before_photos.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                    <Image className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No before photos yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {bookingPhotos.before_photos.map((photo) => (
                      <div key={photo.photo_id} className="relative group">
                        <img 
                          src={`${API_URL}${photo.url}`} 
                          alt="Before repair" 
                          className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          onClick={() => handleDeletePhoto(photo.photo_id)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* After Photos */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    After Repair
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload('after', e.target.files)}
                      disabled={uploadingPhotos}
                    />
                    <Button size="sm" variant="outline" asChild disabled={uploadingPhotos}>
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingPhotos ? 'Uploading...' : 'Upload'}
                      </span>
                    </Button>
                  </label>
                </div>
                
                {bookingPhotos.after_photos.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                    <Image className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No after photos yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {bookingPhotos.after_photos.map((photo) => (
                      <div key={photo.photo_id} className="relative group">
                        <img 
                          src={`${API_URL}${photo.url}`} 
                          alt="After repair" 
                          className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          onClick={() => handleDeletePhoto(photo.photo_id)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 text-center">
                  Photos help build trust with customers and document your work quality.
                  <br />Supported formats: JPG, PNG, WEBP
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default MechanicDashboard;
